import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { AccountHead, AccountSection, goLogin, useAccountUi } from "../../account/accountUi";
import { postJson, readView, useDocumentTitle, useHeadingFocus, useSpaLinks } from "../../config/common";
import { text, type TeamUi } from "../../team/teamText";
import { useFeedback } from "../../team/TeamUi";
import { OptionCards } from "../../users/formParts";
import { UsersError, UsersFrame } from "../../users/usersUi";

// Preferencias (/portal/preferencias): idioma y tema (Preferencias.razor), con tarjetas C08 a la vista en lugar de dos
// <select>. Al guardar, Identity las conserva para cada ingreso y el servidor deja las cookies de ayuda; la página se
// vuelve a cargar para que todo el portal cambie de idioma o tema a la vez, y el toast lo confirma ya cargada.

type Prefs = { locale: string; theme: string };

const savedFlag = "guardado";

export function PreferencesPage({ locale }: { locale: string }) {
  const ui = useAccountUi(locale);
  const view = useQuery({
    queryKey: ["account", "preferences"],
    queryFn: () => readView<Prefs>("/api/portal/account/preferences"),
    staleTime: 0,
  });
  useSpaLinks();
  useHeadingFocus(!!ui && !!view.data);
  useDocumentTitle(ui ? text(ui, "NAV_PREFS") : null);
  if (!ui) return null;
  if (view.data?.status === 401) {
    goLogin();
    return null;
  }
  const ready = !!view.data || view.isError;
  const data = view.data?.status === 200 ? view.data.body : null;
  return (
    <UsersFrame ui={ui} ready={ready}>
      <AccountHead ui={ui} title={text(ui, "NAV_PREFS")} sub={text(ui, "ACC_PREFS_SUB")} />
      {data ? (
        <PreferencesForm ui={ui} saved={data} />
      ) : (
        <UsersError
          message={text(ui, "TEAM_ERR_GENERIC")}
          retryLabel={text(ui, "CFG1_RETRY")}
          onRetry={() => void view.refetch()}
        />
      )}
    </UsersFrame>
  );
}

/** Valor de una cookie de ayuda del navegador; vacío si no está. */
function helperCookie(name: string): string {
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : "";
}

/**
 * Lo que la página está mostrando de verdad: las cookies de ayuda que aplica theme-init.js. Pueden diferir de lo guardado
 * en Identity cuando otro portal del mismo dominio (Platform Admin en DEV, en localhost) escribió la misma cookie.
 */
function appliedPrefs(saved: Prefs): Prefs {
  return {
    locale: helperCookie("bheli-locale") || saved.locale,
    theme: (helperCookie("bheli-theme") || saved.theme).toUpperCase(),
  };
}

function PreferencesForm({ ui, saved }: { ui: TeamUi; saved: Prefs }) {
  const T = (key: string) => text(ui, key);
  const feedback = useFeedback();
  const [draft, setDraft] = useState(saved);
  const [applied] = useState(() => appliedPrefs(saved));
  // Se puede guardar si la elección cambia lo guardado o lo que se ve: elegir «Claro» con la página en oscuro (cookie
  // escrita por otro portal) vuelve a escribir la cookie aunque Identity ya tuviera «Claro».
  const unchanged =
    draft.locale === saved.locale &&
    draft.theme === saved.theme &&
    draft.locale === applied.locale &&
    draft.theme === applied.theme;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Vuelta de la recarga tras guardar: el toast en el idioma nuevo y la dirección limpia.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(savedFlag)) return;
    url.searchParams.delete(savedFlag);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    feedback.show(T("ACC_PREFS_SAVED"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const reply = await postJson<{ saved?: boolean; errorKey?: string }>("/api/portal/account/preferences", draft);
      if (reply.status === 401) return goLogin();
      if (reply.status === 200 && reply.body?.saved) {
        window.location.assign(`/portal/preferencias?${savedFlag}=1`);
        return;
      }
      setError(T(reply.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
      setBusy(false);
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
      setBusy(false);
    }
  };

  return (
    <form className="tm-form tm-user-form acc-form" noValidate onSubmit={(event) => void submit(event)}>
      {error ? <UsersError message={error} /> : null}
      <AccountSection id="prefs-locale-title" title={T("PREFS_LOCALE")} part="idioma">
        <OptionCards
          labelledBy="prefs-locale-title"
          name="locale"
          value={draft.locale}
          onChange={(value) => setDraft({ ...draft, locale: value })}
          options={[
            { value: "es-PE", title: T("LANG_ES_PE"), line: T("ACC_LANG_ES_LINE") },
            { value: "en-US", title: T("LANG_EN_US"), line: T("ACC_LANG_EN_LINE") },
          ]}
        />
      </AccountSection>
      <AccountSection id="prefs-theme-title" title={T("PREFS_THEME")} part="tema">
        <OptionCards
          labelledBy="prefs-theme-title"
          name="theme"
          value={draft.theme}
          onChange={(value) => setDraft({ ...draft, theme: value })}
          options={[
            { value: "SYSTEM", title: T("THEME_SYSTEM"), line: T("ACC_THEME_SYSTEM_LINE") },
            { value: "LIGHT", title: T("THEME_LIGHT"), line: T("ACC_THEME_LIGHT_LINE") },
            { value: "DARK", title: T("THEME_DARK"), line: T("ACC_THEME_DARK_LINE") },
          ]}
        />
      </AccountSection>
      <div className="tm-form-actions" data-fixed-actions="" data-part="acciones">
        <button
          type="submit"
          className="tm-btn primary"
          disabled={busy || unchanged}
          aria-busy={busy ? "true" : undefined}
        >
          {T("ACTION_SAVE")}
        </button>
      </div>
    </form>
  );
}
